import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Header from '../components/Header';
import '../index.css';
import apiClient from '../services/apiClient';
import { CircularProgress, Card, CardContent, Typography, Box } from '@mui/material';
import { getCurrency } from '../services/tableHelper';
import { useSelector } from 'react-redux';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { CreateButton } from '../components/SharedComponents';
import DatePeriodSelector, { Period } from '../components/DatePeriodSelector';
import { format, subDays } from 'date-fns';
import CustomTable, { Column } from '../components/CustomTable';
import ReactGA from 'react-ga4';

const Wrapper = styled.div`
	background-color: white;
	width: 100%;
	min-height: 100vh;
	padding: 20px 20px;
	box-sizing: border-box;
	@media (max-width: 768px) {
		padding: 20px 20px;
	}
`;

const MetricCard = styled(Card)`
	height: 100%;
	.MuiCardContent-root {
		padding: 20px;
		&:last-child {
			padding-bottom: 20px;
		}
	}
`;

const MetricValue = styled(Typography)`
	font-size: 2rem;
	font-weight: bold;
	color: #1976d2;
	margin: 8px 0;
`;

const MetricLabel = styled(Typography)`
	color: #666;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.5px;
`;

const PaymentStatusCard = styled(Card)`
	margin: 16px 0;
	.MuiCardContent-root {
		padding: 16px;
	}
`;

interface ReportData {
	group_info: {
		id: number;
		name: string;
		customer_count: number;
	};
	date_range: {
		start_date: string;
		end_date: string;
	};
	summary: {
		total_orders: number;
		active_customers: number;
		total_sales: number;
		total_paid: number;
		pending_payment: number;
		average_order_value: number;
		payment_completion_rate: number;
	};
	payment_breakdown: Array<{
		status: string;
		orders: number;
		amount: number;
		percentage: number;
	}>;
	top_products: Array<{
		product_id: number;
		product_name: string;
		total_quantity_sold: number;
		times_ordered: number;
		total_revenue: number;
	}>;
	customer_activity: Array<{
		customer_id: number;
		customer_name: string;
		email: string;
		phone: string;
		total_orders: number;
		total_spent: number;
		total_paid: number;
		pending_amount: number;
	}>;
	daily_sales: Array<{
		date: string;
		total_sales: number;
		orders_count: number;
		paid_amount: number;
		pending_amount: number;
	}>;
}

export default function CustomerGroupReports() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));

	const [reportData, setReportData] = useState<ReportData | null>(null);
	const [fetchState, setFetchState] = useState<'loading' | 'success' | 'failed'>('loading');
	const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
	const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

	const fetchReport = useCallback(async () => {
		if (!id) return;

		setFetchState('loading');
		try {
			const response = await apiClient.get(`/customer-groups/${id}/reports`, {
				params: {
					start_date: startDate,
					end_date: endDate,
				},
			});
			setReportData(response.data);
			setFetchState('success');
		} catch (error) {
			console.error('Error fetching customer group report:', error);
			setFetchState('failed');
		}
	}, [id, startDate, endDate]);

	useEffect(() => {
		fetchReport();
		ReactGA.send({
			hitType: 'pageview',
			page: `/customer-groups/${id}/reports`,
			title: 'Customer Group Reports Page',
		});
	}, [fetchReport, id]);

	const handleDateRangeChange = useCallback((from: Date, to: Date, period: Period) => {
		const newStartDate = format(from, 'yyyy-MM-dd');
		const newEndDate = format(to, 'yyyy-MM-dd');
		setStartDate(newStartDate);
		setEndDate(newEndDate);
	}, []);

	const exportReport = useCallback(async () => {
		if (!id || !reportData) return;

		try {
			const response = await apiClient.get(`/customer-groups/${id}/reports/export`, {
				params: {
					start_date: startDate,
					end_date: endDate,
					format: 'pdf',
				},
				responseType: 'blob',
			});

			// Create download link
			const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute(
				'download',
				`customer-group-${reportData.group_info.name}-report-${startDate}-to-${endDate}.pdf`
			);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			ReactGA.event({
				category: 'Customer Group Reports',
				action: 'Exported Report',
				label: `Group ID: ${id}`,
			});
		} catch (error) {
			console.error('Error exporting report:', error);
		}
	}, [id, reportData, startDate, endDate]);

	// Top Products table columns
	const topProductsColumns: Column[] = useMemo(
		() => [
			{
				field: 'product_name',
				headerName: 'Product',
				minWidth: 200,
				flex: 2,
				headerClassName: 'table-header',
				cellClassName: 'font-medium',
			},
			{
				field: 'total_quantity_sold',
				headerName: 'Qty Sold',
				minWidth: 100,
				flex: 1,
				headerClassName: 'table-header',
				cellClassName: 'text-center',
			},
			{
				field: 'times_ordered',
				headerName: 'Times Ordered',
				minWidth: 120,
				flex: 1,
				headerClassName: 'table-header',
				cellClassName: 'text-center',
			},
			{
				field: 'total_revenue',
				headerName: 'Revenue',
				minWidth: 120,
				flex: 1,
				headerClassName: 'table-header',
				cellClassName: 'text-right font-medium',
				renderCell: (params) => `${currency}${Number(params.value).toFixed(2)}`,
			},
		],
		[currency]
	);

	// Customer Activity table columns
	const customerActivityColumns: Column[] = useMemo(
		() => [
			{
				field: 'customer_name',
				headerName: 'Customer',
				minWidth: 180,
				flex: 2,
				headerClassName: 'table-header',
				cellClassName: 'font-medium',
			},
			{
				field: 'total_orders',
				headerName: 'Orders',
				minWidth: 80,
				flex: 1,
				headerClassName: 'table-header',
				cellClassName: 'text-center',
			},
			{
				field: 'total_spent',
				headerName: 'Total Spent',
				minWidth: 120,
				flex: 1,
				headerClassName: 'table-header',
				cellClassName: 'text-right font-medium',
				renderCell: (params) => `${currency}${Number(params.value).toFixed(2)}`,
			},
			{
				field: 'total_paid',
				headerName: 'Paid',
				minWidth: 120,
				flex: 1,
				headerClassName: 'table-header',
				cellClassName: 'text-right text-green-600 font-medium',
				renderCell: (params) => `${currency}${Number(params.value).toFixed(2)}`,
			},
			{
				field: 'pending_amount',
				headerName: 'Pending',
				minWidth: 120,
				flex: 1,
				headerClassName: 'table-header',
				cellClassName: 'text-right text-orange-600 font-medium',
				renderCell: (params) => `${currency}${Number(params.value).toFixed(2)}`,
			},
		],
		[currency]
	);

	if (fetchState === 'loading') {
		return (
			<Wrapper>
				<Header title="Customer Group Report" />
				<div className="flex justify-center items-center h-64">
					<CircularProgress />
				</div>
			</Wrapper>
		);
	}

	if (fetchState === 'failed' || !reportData) {
		return (
			<Wrapper>
				<Header title="Customer Group Report" />
				<div className="flex justify-center items-center h-64">
					<div className="text-red-500">Failed to load report. Please try again.</div>
				</div>
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			<Header
				title={`${reportData.group_info.name} - Report`}
				description={`Performance report for ${reportData.group_info.customer_count} customers`}
				headerComponents={[
					<CreateButton key="export-button" onClick={exportReport}>
						<FileDownloadIcon style={{ fontSize: '18px' }} />
						<span>Export PDF</span>
					</CreateButton>,
					<CreateButton key="back-button" onClick={() => navigate('/customers/groups')} outline>
						<ArrowBackIcon style={{ fontSize: '18px' }} />
						<span>Back to Groups</span>
					</CreateButton>,
				]}
			/>

			{/* Date Range Selector */}
			<Box mt={3} mb={3} display="flex" justifyContent="flex-end">
				<DatePeriodSelector onDateRangeChange={handleDateRangeChange} initialPeriod="This month" />
			</Box>

			{/* Summary Metrics */}
			<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
				<MetricCard>
					<CardContent>
						<MetricLabel>Total Orders</MetricLabel>
						<MetricValue>{reportData.summary.total_orders}</MetricValue>
					</CardContent>
				</MetricCard>
				<MetricCard>
					<CardContent>
						<MetricLabel>Total Sales</MetricLabel>
						<MetricValue>
							{currency}
							{Number(reportData.summary.total_sales).toFixed(2)}
						</MetricValue>
					</CardContent>
				</MetricCard>
				<MetricCard>
					<CardContent>
						<MetricLabel>Average Order Value</MetricLabel>
						<MetricValue>
							{currency}
							{Number(reportData.summary.average_order_value).toFixed(2)}
						</MetricValue>
					</CardContent>
				</MetricCard>
				<MetricCard>
					<CardContent>
						<MetricLabel>Payment Rate</MetricLabel>
						<MetricValue>{Number(reportData.summary.payment_completion_rate).toFixed(1)}%</MetricValue>
					</CardContent>
				</MetricCard>
			</div>

			{/* Payment Status Breakdown */}
			<PaymentStatusCard>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						Payment Status Breakdown
					</Typography>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						{reportData.payment_breakdown.map((item, index) => (
							<div key={index}>
								<Box
									p={2}
									border={1}
									borderColor={item.status === 'Paid' ? 'success.main' : 'warning.main'}
									borderRadius={1}
									sx={{ bgcolor: item.status === 'Paid' ? '#e8f5e8' : '#fff3e0' }}
								>
									<Typography
										variant="subtitle2"
										color={item.status === 'Paid' ? 'success.dark' : 'warning.dark'}
									>
										{item.status}
									</Typography>
									<Typography
										variant="h6"
										color={item.status === 'Paid' ? 'success.dark' : 'warning.dark'}
									>
										{currency}
										{Number(item.amount).toFixed(2)}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{item.orders} orders ({Number(item.percentage).toFixed(1)}%)
									</Typography>
								</Box>
							</div>
						))}
					</div>
				</CardContent>
			</PaymentStatusCard>

			{/* Top Products */}
			<Box mb={4}>
				<Typography variant="h6" gutterBottom>
					Top Products
				</Typography>
				<CustomTable
					columns={topProductsColumns}
					objects={reportData.top_products}
					fetchState="success"
					initialRowsPerPage={10}
				/>
			</Box>

			{/* Customer Activity */}
			<Box mb={4}>
				<Typography variant="h6" gutterBottom>
					Customer Activity
				</Typography>
				<CustomTable
					columns={customerActivityColumns}
					objects={reportData.customer_activity}
					fetchState="success"
					initialRowsPerPage={10}
				/>
			</Box>
		</Wrapper>
	);
}
